import { FormInput, FormLabel, Form, Button, Text, FormFieldGroup, FormSelect } from '$components'
import z from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantActiveQueryKey, tenantOnboardingUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { TenantOnboardingStage, TenantType } from '$api-client/types.gen'

export enum Input {
  BusinessName = 'business_name',
  BusinessType = 'business_type',
}

const schema = z.object({
  [Input.BusinessName]: z.string().min(1, 'Business name is required').trim(),
  [Input.BusinessType]: z.object({
    id: z.string(),
    value: z.string(),
  }),
})

export type BusinessDetailsFormValues = z.infer<typeof schema>

export function BusinessDetailsPage() {
  const queryClient = useQueryClient()

  const { mutate: updateTenant, isPending: isUpdatingTenant } = useMutation({
    ...tenantOnboardingUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantActiveQueryKey(),
      })
    },
  })

  const onSubmit = async (values: BusinessDetailsFormValues) => {
    updateTenant({
      body: {
        stage: TenantOnboardingStage.FINISHED,
        business_name: values[Input.BusinessName],
        business_type: values[Input.BusinessType].id as TenantType,
      },
    })
  }

  return (
    <div className="w-full h-dvh flex items-center justify-center">
      <div className="flex flex-col w-full max-w-[420px] px-6">
        <Text
          type="h1"
          className="mb-8 mt-2"
        >
          Complete your account setup
        </Text>

        <Form
          onSubmit={onSubmit}
          schema={schema}
        >
          {() => (
            <div className="flex flex-col gap-4">
              <FormFieldGroup>
                <FormLabel htmlFor={Input.BusinessName}>Business name</FormLabel>
                <FormInput
                  name={Input.BusinessName}
                  placeholder="Enter business name"
                />
              </FormFieldGroup>

              <FormFieldGroup>
                <FormLabel htmlFor={Input.BusinessType}>Business type</FormLabel>
                <FormSelect
                  name={Input.BusinessType}
                  placeholder="Select business type"
                  options={Object.values(TenantType).map((type) => ({
                    id: type,
                    value: type,
                  }))}
                />
              </FormFieldGroup>

              <Button
                type="submit"
                size="lg"
                className="mt-4"
                isLoading={isUpdatingTenant}
                disabled={isUpdatingTenant}
              >
                Continue
              </Button>
            </div>
          )}
        </Form>
      </div>
    </div>
  )
}
